import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { Check, Copy, ExternalLink, PenLine, PartyPopper, Share2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface PublishedSuccessProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId?: string;
  pubkey?: string;
  dTag?: string;
  title?: string;
  onWriteAnother: () => void;
}

export function PublishedSuccess({
  open,
  onOpenChange,
  eventId,
  pubkey,
  dTag,
  title,
  onWriteAnother,
}: PublishedSuccessProps) {
  const [copied, setCopied] = useState(false);

  const naddr = pubkey && dTag 
    ? nip19.naddrEncode({
        kind: 30023,
        pubkey,
        identifier: dTag,
      })
    : null;

  const shareUrl = naddr 
    ? `https://njump.me/${naddr}` 
    : eventId 
      ? `https://njump.me/${nip19.noteEncode(eventId)}` 
      : null;

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: 'Copied!',
        description: 'Link copied to clipboard.',
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (shareUrl && navigator.share) {
      try {
        await navigator.share({
          title: title || 'Check out my article on Nostr',
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled or share failed
        console.error('Share failed:', error);
      }
    } else if (shareUrl) {
      handleCopy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-w-[calc(100vw-2rem)] mx-auto overflow-hidden">
        <DialogHeader className="space-y-4">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <PartyPopper className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          
          {/* Title */}
          <DialogTitle className="text-2xl font-serif text-center text-foreground">
            Published Successfully!
          </DialogTitle>
          
          {/* Description */}
          <DialogDescription className="text-base text-center text-muted-foreground">
            {title ? (
              <>Your article "<span className="font-medium text-foreground">{title}</span>" is now live on Nostr.</>
            ) : (
              <>Your article is now live on Nostr.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {shareUrl && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Share your article</label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0 p-3 bg-muted text-foreground rounded-lg text-sm font-mono break-all overflow-hidden">
                  {shareUrl}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  className={cn(
                    "shrink-0 transition-colors",
                    copied && "bg-green-100 text-green-600 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800"
                  )}
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {shareUrl && (
              <>
                <Button
                  variant="outline"
                  className="w-full gap-2 text-foreground"
                  onClick={handleShare}
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-2 text-foreground"
                  asChild
                >
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                    View Article
                  </a>
                </Button>
              </>
            )}
          </div>

          <div className="pt-2 border-t border-border">
            <Button
              onClick={() => {
                onOpenChange(false);
                onWriteAnother();
              }}
              className="w-full gap-2"
            >
              <PenLine className="w-4 h-4" />
              Write Another Article
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
